// src/react-auth0-spa.js
import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import createAuth0Client from "@auth0/auth0-spa-js";
import { adminEmails, staffEmails } from "./config"; // Import email arrays from config

const DEFAULT_REDIRECT_CALLBACK = () => {
  // Save full original URL and restore it instead of just pathname
  const returnUrl = localStorage.getItem('auth_return_url') || window.location.pathname;
  window.history.replaceState({}, document.title, returnUrl);
  localStorage.removeItem('auth_return_url');
};

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);

      if (window.location.search.includes("code=")) {
        try {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          
          // If we have appState with targetUrl, use it, otherwise check localStorage
          if (appState && appState.targetUrl) {
            // Store it in localStorage in case we need it after a page refresh
            localStorage.setItem('auth_return_url', appState.targetUrl);
          }
          
          onRedirectCallback(appState);
        } catch (err) {
          console.error("Error handling redirect callback:", err);
          // Still try to recover and proceed with auth even if callback handling fails
        }
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        
        if (user.email && staffEmails.includes(user.email)) {
          user.isOfcStaff = true;
          if (adminEmails.includes(user.email)) {
            user.isOfcAdmin = true;
          }
        }
        setUser(user);
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = useCallback(async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);
  }, [auth0Client]);

  const handleRedirectCallback = useCallback(async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  }, [auth0Client]);

  const getIdTokenClaims = useCallback((...p) => auth0Client.getIdTokenClaims(...p), [auth0Client]);
  const loginWithRedirect = useCallback((...p) => auth0Client.loginWithRedirect(...p), [auth0Client]);
  const getTokenSilently = useCallback((...p) => auth0Client.getTokenSilently(...p), [auth0Client]);
  const getTokenWithPopup = useCallback((...p) => auth0Client.getTokenWithPopup(...p), [auth0Client]);
  const logout = useCallback((...p) => auth0Client.logout(...p), [auth0Client]);
  return (
    <Auth0Context.Provider
      value={useMemo(() => ({
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims,
        loginWithRedirect,
        getTokenSilently,
        getTokenWithPopup,
        logout
      }), [
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims,
        loginWithRedirect,
        getTokenSilently,
        getTokenWithPopup,
        logout
      ])}
    >
      {children}
    </Auth0Context.Provider>
  );
};
